export type ProductionWeekOption = {
  week: string;
  iso_week: string;
  label: string;
};

export type ProductionWeekChipStatus = 'empty' | 'covered' | 'pending' | 'urgent';

export const PRODUCTION_WEEK_PAGE_SIZE = 15;

export function productionScopeQueryParams(
  week: string | null | undefined,
  soExternalId?: string | null,
): { production_week?: string; so_external_id?: string } {
  const params: { production_week?: string; so_external_id?: string } = {};
  if (week) {
    params.production_week = week;
  }
  if (soExternalId) {
    params.so_external_id = soExternalId;
  }
  return params;
}

export function formatWeekDateRange(
  mondayIso: string,
  locale: string,
): { mondayLabel: string; sundayLabel: string; sundayIso: string } {
  const monday = new Date(`${mondayIso}T00:00:00`);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  return {
    mondayLabel: monday.toLocaleDateString(locale, opts),
    sundayLabel: sunday.toLocaleDateString(locale, opts),
    sundayIso: toIsoDate(sunday),
  };
}

export function nextProductionWeekOption(
  weeks: ProductionWeekOption[],
  currentWeek: string,
): ProductionWeekOption | undefined {
  const index = weeks.findIndex((item) => item.week === currentWeek);
  if (index < 0 || index >= weeks.length - 1) return undefined;
  return weeks[index + 1];
}

export function productionScopeSessionKey(
  week: string,
  soExternalId?: string | null,
): string {
  return `${week}|${soExternalId ?? ''}`;
}

export function toIsoDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseIsoDateLocal(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function isoMondayFromDate(value: Date): Date {
  const copy = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const dow = copy.getDay();
  copy.setDate(copy.getDate() + (dow === 0 ? -6 : 1 - dow));
  return copy;
}

export function addIsoWeeks(monday: Date, weeks: number): Date {
  const next = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate());
  next.setDate(next.getDate() + weeks * 7);
  return next;
}

export function isoWeekParts(monday: Date): { year: number; week: number; mondayIso: string; isoWeek: string } {
  const thursday = addIsoWeeks(monday, 0);
  thursday.setDate(monday.getDate() + 3);
  const year = thursday.getFullYear();
  const jan4Monday = isoMondayFromDate(new Date(year, 0, 4));
  const week = Math.round((monday.getTime() - jan4Monday.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
  const mondayIso = toIsoDate(monday);
  return {
    year,
    week,
    mondayIso,
    isoWeek: `${year}-W${String(week).padStart(2, '0')}`,
  };
}

export function firstIsoMondayOfYear(year: number): Date {
  return isoMondayFromDate(new Date(year, 0, 4));
}

export function lastIsoMondayOfYear(year: number): Date {
  return isoMondayFromDate(new Date(year, 11, 28));
}

export function listIsoMondaysInYear(start: Date, year: number, count: number): Date[] {
  const last = lastIsoMondayOfYear(year);
  const weeks: Date[] = [];
  let cursor = start < firstIsoMondayOfYear(year) ? firstIsoMondayOfYear(year) : start;
  while (weeks.length < count && cursor <= last) {
    weeks.push(cursor);
    cursor = addIsoWeeks(cursor, 1);
  }
  return weeks;
}

export function defaultWeekWindowStart(year: number, today: Date = new Date()): Date {
  const first = firstIsoMondayOfYear(year);
  const currentMonday = isoMondayFromDate(today);
  const currentYear = isoWeekParts(currentMonday).year;
  if (year === currentYear && currentMonday >= first) {
    return currentMonday > lastIsoMondayOfYear(year) ? first : currentMonday;
  }
  return first;
}

export function windowStartContainingWeek(
  monday: Date,
  year: number,
  today: Date = new Date(),
): Date {
  const first = firstIsoMondayOfYear(year);
  const startBase = defaultWeekWindowStart(year, today);
  if (monday >= startBase) {
    let start = startBase;
    while (addIsoWeeks(start, PRODUCTION_WEEK_PAGE_SIZE) <= monday) {
      start = addIsoWeeks(start, PRODUCTION_WEEK_PAGE_SIZE);
    }
    return start;
  }
  let start = startBase;
  while (start > monday) {
    start = addIsoWeeks(start, -PRODUCTION_WEEK_PAGE_SIZE);
  }
  return start < first ? first : start;
}

export function productionWeekChipStatus(
  card?: {
    urgent_products?: number;
    needs_reorder_products?: number;
    pending?: boolean;
  } | null,
  isInWindowCovered = false,
): ProductionWeekChipStatus {
  if (card) {
    if ((card.urgent_products ?? 0) > 0) return 'urgent';
    if ((card.needs_reorder_products ?? 0) > 0 || card.pending) return 'pending';
  }
  return isInWindowCovered ? 'covered' : 'empty';
}
